import React, { useState } from "react";
import { Box, Typography, Container, Drawer } from "@mui/material";
import { CheckCircle, Warning } from "@mui/icons-material";
import { usePayrollCycleRun } from "../../../PayrollCycleRunContext";
import TimelineItem from "./TimelineItem";
import WarningCard from "./WarningCard";
import { customAPI } from "services/utils/CustomAPI";
import { useSelector } from "react-redux";
import { showToast } from "services/utils/Status";
import { useFetch } from "services/hooks/useFetch";
import { Loader } from "components/Loader";
import { drawerStyle } from "services/constants";
import { EmployeeDetails } from "./EmployeeDetails";

const Logs = () => {
  const {
    payScheduleId,
    paymentBasedOn,
    setRecallSummaryApi,
    setGetUpdatedData,
    setActiveTab,
    setSocketCompleted,
  } = usePayrollCycleRun();
  const { domain } = useSelector((state) => state.tokenReducer);
  const [viewDetails, setViewDetails] = useState(false);
  const [empData, setEmpData] = useState([]);
  const {
    result: logData,
    loading,
    setLoad: reCallApi,
  } = useFetch(
    `${domain}payroll/operations/payrollprocessing/payrollruncycle/summarized_log_details/?pay_schedule_id=${payScheduleId}`
  );

  const hanldeRerun = async () => {
    const response = await customAPI(
      `${domain}payroll/operations/payrollprocessing/payrollruncycle/intiate_payrun/`,
      "POST",
      {
        pay_schedule_id: payScheduleId,
        payment_based_on: paymentBasedOn,
      }
    );

    if (response.ok) {
      setActiveTab(0);
      showToast("success", response?.responseDetails?.message);
      reCallApi(true);
      setRecallSummaryApi(true);
      setGetUpdatedData(true);
      setSocketCompleted(true);
    } else {
      showToast("error", response?.message);
    }
  };

  const typeKeyMap = {
    "Missing Entries": "neglect_missing_entry",
    "Pending Leaves": "neglect_leave",
    "Pending Overtime": "neglect_overtime",
    "Pending Weekoff": "neglect_weekoff",
  };

  const handleNeglectandContinue = async (type) => {
    const response = await customAPI(
      `${domain}payroll/operations/payrollprocessing/payrollruncycle/update-payschedule-field/`,
      "PUT",
      {
        pay_schedule_id: payScheduleId,
        ...(typeKeyMap[type] && { [typeKeyMap[type]]: true }),
      }
    );
    if (response.ok) {
      showToast("success", response?.responseDetails?.message);
      hanldeRerun();
    } else {
      showToast("error", response?.message);
    }
  };

  const renderIcon = (type) => {
    const iconProps = { sx: { fontSize: 20 } };

    switch (type) {
      case "success":
        return <CheckCircle {...iconProps} />;
      case "warning":
        return <Warning {...iconProps} />;
      default:
        return <CheckCircle {...iconProps} />;
    }
  };

  const getIconColor = (type) => {
    switch (type) {
      case "success":
        return "success";
      case "warning":
        return "warning";
      default:
        return "primary";
    }
  };

  const handleViewDetails = (data) => {
    setViewDetails(true);
    setEmpData(data);
  };

  const renderWarningCards = (item, logs, index) => {
    if (!logs || logs.length === 0) return null;
    const isLastLogGroup = index === 0 && !!logData[index]?.logs?.length;

    return (
      <Box sx={{ display: "flex", flexDirection: "column", gap: 2, mt: 2 }}>
        <WarningCard
          key={`warning-${logs[0].name}-${logs[0].date}`}
          message={item?.status_description}
          logData={logs}
          linkAction={() => handleViewDetails(item)}
          onRerun={() => hanldeRerun()}
          neglectAndContinue={() => handleNeglectandContinue(item?.event_type)}
          isLastWarning={isLastLogGroup}
        />
      </Box>
    );
  };

  return loading ? (
    <Loader />
  ) : (
    <React.Fragment>
      <Container
        maxWidth="100%"
        sx={{ py: 4, height: "65vh", display: "flex", flexDirection: "column" }}
      >
        <Box
          sx={{
            position: "relative",
            flex: 1,
            overflow: "hidden",
            border: "1px solid",
            borderColor: "grey.200",
            borderRadius: 2,
            backgroundColor: "grey.50",
          }}
        >
          <Box
            sx={{
              position: "relative",
              height: "100%",
              overflow: "auto",
              p: 3,
            }}
          >
            <Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
              {logData.length === 0 ? (
                <Typography
                  sx={{ textAlign: "center", color: "text.secondary", mt: 4 }}
                >
                  No logs data available
                </Typography>
              ) : (
                logData.map((item, index) => (
                  <TimelineItem
                    key={item.id || index}
                    icon={renderIcon(
                      item?.logs?.length === 0 ? "success" : "warning"
                    )}
                    iconColor={getIconColor(
                      item?.logs?.length === 0 ? "success" : "warning"
                    )}
                    image_url={item.image_url}
                    timestamp={item.date_time}
                    changedBy={item.changed_by}
                    isLast={index === logData.length - 1}
                  >
                    {item.event_type && (
                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          gap: 1,
                          mt: 1,
                        }}
                      >
                        <Typography
                          variant="body2"
                          sx={{ color: "text.secondary" }}
                        >
                          Status:
                        </Typography>
                        <Box
                          sx={{
                            px: 1,
                            py: 0.5,
                            backgroundColor: "grey.200",
                            borderRadius: 1,
                            fontSize: "0.75rem",
                            textTransform: "capitalize",
                          }}
                        >
                          {item.event_type}
                        </Box>
                      </Box>
                    )}

                    {item?.logs?.length === 0 && (
                      <Box sx={{ mt: 1 }}>
                        <Typography
                          variant="body2"
                          sx={{ color: "text.primary", paddingTop: 0.5 }}
                        >
                          {item?.status_description}
                        </Typography>
                      </Box>
                    )}

                    {renderWarningCards(item, item?.logs, index)}
                  </TimelineItem>
                ))
              )}
            </Box>
          </Box>
        </Box>
      </Container>
      <Drawer
        anchor="right"
        open={viewDetails}
        onClose={() => setViewDetails(false)}
        sx={drawerStyle}
      >
        {
          <EmployeeDetails
            onClose={() => setViewDetails(false)}
            empData={empData}
          />
        }
      </Drawer>
    </React.Fragment>
  );
};

export default Logs;
